import React, { useCallback, useState } from 'react';

import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  atoms,
  colors,
  Field,
  HStack,
  useToastContext,
  VStack,
  Banner,
} from '@rtkwlf/fenrir-react';
import { faUserCircle } from '@rtkwlf/pro-solid-svg-icons/faUserCircle';
import type { TicketWithComments } from '@rtkwlf/rendall';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useContactInfo } from '@apis/rendall/queries/use-contact-info';
import { useCreateTicketComment } from '@apis/rendall/queries/use-create-ticket-comment';
import { useSolveTicket } from '@apis/rendall/queries/use-solve-ticket';
import type { UploadFileFunctionType } from '@packages/components/fileAttachment/FileAttachment';
import { FileAttachment } from '@packages/components/fileAttachment/FileAttachment';
import { PageBlocker } from '@packages/components/page-blocker/page-blocker';
import { useUploadFile } from '@packages/hooks/use-upload-file/use-upload-file';
import { getFormattedPhoneAndEmail } from '@packages/utils/phone';
import { TOAST_DURATION_SHORT } from 'ae-constants/toast';

import { ActionButtons } from '../actionButtons/ActionButtons';
import { FormTextArea } from '../formTextArea/FormTextArea';
import type { TicketRuleTypeSelectValue } from '../ticket-rule-type-select/use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';
import { CloseAction, type ActionType } from '../ticketAction.constants';
import { ActionErrorMessage } from '../ticketAction.utils';

import { TicketAlertActionForm } from './ticket-alert-action-form/ticket-alert-action-form';
import { useTicketActionConfig } from './use-ticket-action-form-config/use-ticket-action-form-config';
type ParamTypes = {
  ticketId: string;
};

type TicketActionFormProps = {
  onUpdateSuccess: (data: TicketWithComments) => void;
  actionType: ActionType;
  ruleType?: TicketRuleTypeSelectValue | '';
  ticketId?: string;
};

export const TicketActionForm: React.FC<TicketActionFormProps> = ({
  onUpdateSuccess,
  actionType,
  ruleType,
  ticketId: ticketIdProps = '',
}) => {
  const { t } = useTranslation('ticket');
  const { ticketId = ticketIdProps } = useParams<ParamTypes>();
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [fileCounter, setFileCounter] = useState(0);

  const {
    bannerProps,
    hasCancelButton,
    shouldComment,
    canAttachFile,
    closureReason,
    submitButtonText,
  } = useTicketActionConfig({ actionType, ruleType });

  const isAddCommentActionWithoutClosing = shouldComment && !closureReason;

  const { addToast } = useToastContext();
  const { data: contactInfo } = useContactInfo();

  const form = useForm({
    defaultValues: { comment: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      Yup.object()
        .shape({
          comment: shouldComment
            ? Yup.string().required(t('addCommentRequiredError'))
            : Yup.string(),
        })
        .required(),
    ),
  });

  const {
    resetFiles,
    fileTokens,
    isPending: isFileUploadPending,
    removeFile,
    uploadFile,
  } = useUploadFile();

  const handleError = useCallback(() => {
    setErrorMessage(
      getFormattedPhoneAndEmail(
        isAddCommentActionWithoutClosing ? t('errorComment') : t('actionError'),
        contactInfo,
      ),
    );
  }, [t, contactInfo, isAddCommentActionWithoutClosing]);

  const resetValues = useCallback(() => {
    resetFiles();
    form.reset();
    setFileCounter((p) => p + 1);
  }, [resetFiles, form]);

  const handleSuccess = useCallback(
    (data: TicketWithComments) => {
      addToast({
        appearance: 'success',
        duration: TOAST_DURATION_SHORT,
        message: isAddCommentActionWithoutClosing
          ? t('actionSuccessToast.createCommentMessage')
          : t('actionSuccessToast.closeTicketMessage', {
              ticketId,
            }),
        title: t('actionSuccessToast.title'),
      });

      resetValues();
      onUpdateSuccess(data);
    },
    [
      addToast,
      resetValues,
      onUpdateSuccess,
      t,
      ticketId,
      isAddCommentActionWithoutClosing,
    ],
  );

  const { mutate: closeTicket, isPending: isCloseTicketRequestPending } =
    useSolveTicket({
      onError: handleError,
      onSuccess: handleSuccess,
    });

  const {
    mutate: createTicketComment,
    isPending: isCreateTicketCommentRequestPending,
  } = useCreateTicketComment({
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const handleSubmit = form.handleSubmit((data) => {
    if (closureReason) {
      closeTicket({
        requestInput: {
          closureReason,
          ...(shouldComment ? { comment: data.comment } : {}),
          ...(canAttachFile ? { attachments: fileTokens } : {}),
        },
        ticketId,
      });
    } else {
      createTicketComment({
        commentInput: {
          attachments: fileTokens,
          body: data.comment,
        },
        ticketId,
      });
    }
  });

  const isPending =
    isCreateTicketCommentRequestPending ||
    isCloseTicketRequestPending ||
    isFileUploadPending;

  if (
    actionType === CloseAction.ManageAlertConfiguration &&
    ruleType &&
    ruleType !== 'other'
  ) {
    return (
      <TicketAlertActionForm
        ruleType={ruleType}
        ticketId={ticketId}
        submitButtonText={submitButtonText}
        isCloseTicketPending={isCloseTicketRequestPending}
        errorMessage={errorMessage}
        onCloseErrorMessage={() => setErrorMessage('')}
      />
    );
  }

  return (
    <>
      <form className={atoms({ width: 'full' })} onSubmit={handleSubmit}>
        <VStack width="full" gap="large">
          <VStack width="full">
            <HStack
              alignItems="flex-start"
              width="full"
              display={shouldComment ? 'flex' : 'none'}
              marginTop="medium"
            >
              <FontAwesomeIcon
                icon={faUserCircle}
                css={css({
                  fontSize: '32px',
                  color: `rgb(${colors.background.brand.default})`,
                })}
              />
              <VStack width="full">
                <Field.Root
                  appearance={
                    form.formState.errors.comment ? 'danger' : undefined
                  }
                  isRequired
                >
                  <Field.Content>
                    <FormTextArea
                      control={form.control}
                      isDisabled={!shouldComment}
                    />
                  </Field.Content>
                  <Field.Message>
                    {form.formState.errors.comment?.message?.toString() || ''}
                  </Field.Message>
                </Field.Root>
                {canAttachFile && (
                  <FileAttachment
                    setErrorMessage={setErrorMessage}
                    uploadFile={uploadFile as UploadFileFunctionType}
                    deleteFile={removeFile}
                    wrapperClass={atoms({ width: 'full' })}
                    resetFilesCounter={fileCounter}
                  />
                )}
                <ActionErrorMessage
                  onClose={() => {
                    setErrorMessage('');
                  }}
                  errorMessage={errorMessage as string}
                />
              </VStack>
            </HStack>
            {bannerProps && <Banner {...bannerProps} />}
          </VStack>
          <ActionButtons
            cancelLabel={t('cancelCommentButton')}
            confirmLabel={submitButtonText}
            isLoading={isPending}
            showCancel={hasCancelButton}
            onCancel={resetValues}
            isActionButtonDisabled={
              actionType === CloseAction.ManageAlertConfiguration && !ruleType
            }
          />
        </VStack>
      </form>
      <PageBlocker
        shouldBlock={form.formState.isDirty}
        confirmText={t('ticket:confirmation.delete')}
        description={t('ticket:confirmation.body')}
        title={t('ticket:confirmation.title')}
      />
    </>
  );
};
